import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "../config/supabase";
import { User } from "@supabase/supabase-js";

interface AdminUsersProps {
  user: User | null;
  darkMode: boolean;
}

type AccountType = "user" | "patient" | "doctor" | "admin";

interface UserRow {
  user_id: string;
  email: string | null;
  name: string | null;
  surname: string | null;
  account_type: AccountType | null;
  treatment_type: string | null;
  created_at: string | null;
}

const AdminUsers: React.FC<AdminUsersProps> = ({ user, darkMode }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [rows, setRows] = useState<UserRow[]>([]);
  const [query, setQuery] = useState("");
  const [activeTab, setActiveTab] = useState<"all" | "doctors">("all");

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const { data, error } = await supabase
          .from("user_data")
          .select(
            "user_id, email, name, surname, account_type, treatment_type, created_at"
          )
          .order("created_at", { ascending: false });
        if (error) throw error;
        setRows((data as unknown as UserRow[]) || []);
      } catch (err: any) {
        console.error("Failed to load users:", err);
        setError(err?.message || "Failed to load users");
      } finally {
        setLoading(false);
      }
    };
    if (user) load();
  }, [user]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = rows;
    if (activeTab === "doctors") {
      list = list.filter((r) => r.account_type === "doctor");
    }
    if (!q) return list;
    return list.filter((r) => {
      const parts = [
        r.email,
        r.name,
        r.surname,
        r.account_type,
        r.treatment_type,
      ]
        .filter(Boolean)
        .map((v) => String(v).toLowerCase());
      return parts.some((p) => p.includes(q));
    });
  }, [rows, query, activeTab]);

  const summary = useMemo(() => {
    const total = rows.length;
    const doctors = rows.filter((r) => r.account_type === "doctor").length;
    const admins = rows.filter((r) => r.account_type === "admin").length;
    // Patients are stored as 'user', 'patient', or NULL — signup never assigns
    // the literal 'patient' value, so counting it directly always yields 0.
    // Everyone who isn't a doctor/admin is a patient.
    const patients = total - doctors - admins;
    return { total, doctors, patients, admins };
  }, [rows]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setActiveTab("all")}
            className={`px-3 py-1.5 rounded-md text-sm font-semibold border ${
              activeTab === "all"
                ? darkMode
                  ? "bg-brand-cyan/10 border-brand-cyan text-brand-cyan"
                  : "bg-brand-cyan/10 border-brand-cyan text-brand-cyan"
                : darkMode
                ? "bg-gray-700 border-gray-600 text-gray-200"
                : "bg-white border-gray-300 text-gray-700"
            }`}
          >
            All Users ({summary.total})
          </button>
          <button
            onClick={() => setActiveTab("doctors")}
            className={`px-3 py-1.5 rounded-md text-sm font-semibold border ${
              activeTab === "doctors"
                ? darkMode
                  ? "bg-brand-cyan/10 border-brand-cyan text-brand-cyan"
                  : "bg-brand-cyan/10 border-brand-cyan text-brand-cyan"
                : darkMode
                ? "bg-gray-700 border-gray-600 text-gray-200"
                : "bg-white border-gray-300 text-gray-700"
            }`}
          >
            Doctors ({summary.doctors})
          </button>
        </div>
        <input
          type="text"
          placeholder="Search by name, email, role..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className={`w-full sm:w-80 px-3 py-2 rounded-md border focus:ring-2 focus:ring-brand-cyan focus:border-transparent ${
            darkMode
              ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
              : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
          }`}
        />
      </div>

      <div
        className={`rounded-xl shadow ${darkMode ? "bg-gray-800" : "bg-white"}`}
      >
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <div
            className={`text-sm ${
              darkMode ? "text-gray-300" : "text-gray-600"
            }`}
          >
            Total: {summary.total} • Doctors: {summary.doctors} • Patients:{" "}
            {summary.patients} • Admins: {summary.admins}
          </div>
        </div>

        {loading ? (
          <div className="p-6">Loading...</div>
        ) : error ? (
          <div className="p-6 text-red-500">{error}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className={darkMode ? "text-gray-300" : "text-gray-700"}>
                  <th className="text-left px-4 py-2">Name</th>
                  <th className="text-left px-4 py-2">Email</th>
                  <th className="text-left px-4 py-2">Role</th>
                  <th className="text-left px-4 py-2">Treatment</th>
                  <th className="text-left px-4 py-2">Created</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr
                    key={r.user_id}
                    className={
                      darkMode
                        ? "border-t border-gray-700 hover:bg-gray-700/50"
                        : "border-t border-gray-200 hover:bg-gray-50"
                    }
                  >
                    <td className="px-4 py-2">
                      <div
                        className={`font-medium ${
                          darkMode ? "text-white" : "text-gray-900"
                        }`}
                      >
                        {r.name || "-"} {r.surname || ""}
                      </div>
                    </td>
                    <td className="px-4 py-2">
                      <div
                        className={darkMode ? "text-gray-300" : "text-gray-700"}
                      >
                        {r.email || "-"}
                      </div>
                    </td>
                    <td className="px-4 py-2">
                      <span
                        className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${
                          r.account_type === "doctor"
                            ? "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-200"
                            : r.account_type === "admin"
                            ? "bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-200"
                            : r.account_type === "patient"
                            ? "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-200"
                            : "bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200"
                        }`}
                      >
                        {r.account_type || "-"}
                      </span>
                    </td>
                    <td className="px-4 py-2">
                      <div
                        className={darkMode ? "text-gray-300" : "text-gray-700"}
                      >
                        {r.treatment_type || "-"}
                      </div>
                    </td>
                    <td className="px-4 py-2">
                      <div
                        className={darkMode ? "text-gray-300" : "text-gray-700"}
                      >
                        {r.created_at
                          ? new Date(r.created_at).toLocaleString()
                          : "-"}
                      </div>
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr>
                    <td className="px-4 py-6 text-center" colSpan={5}>
                      <span
                        className={darkMode ? "text-gray-300" : "text-gray-600"}
                      >
                        No records found
                      </span>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminUsers;
